import { digest } from 'lib0/hash/sha256';

/**
 * SHA256 Checksum utilities for Y.js update integrity verification.
 *
 * Used to detect data corruption during storage/transfer operations.
 * Runs async in background - no UI impact.
 */

/**
 * Compute SHA256 checksum for binary data.
 * Uses native SubtleCrypto API for performance.
 *
 * @param data - Binary data to hash
 * @returns Hex-encoded SHA256 hash
 */
export async function computeChecksum(data: Uint8Array): Promise<string> {
  const bytes = new Uint8Array(data.byteLength);
  bytes.set(data);
  const hashBuffer = globalThis.crypto?.subtle
    ? await globalThis.crypto.subtle.digest('SHA-256', bytes.buffer)
    : digest(bytes);
  const hashArray = new Uint8Array(hashBuffer);
  return Array.from(hashArray)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Verify data integrity against expected checksum.
 *
 * @param data - Binary data to verify
 * @param expected - Expected SHA256 hash (hex-encoded)
 * @returns true if checksum matches, false if corrupted
 */
export async function verifyChecksum(
  data: Uint8Array,
  expected: string
): Promise<boolean> {
  const actual = await computeChecksum(data);
  return actual === expected;
}

/**
 * Checksum verification result with details for debugging.
 */
export interface ChecksumResult {
  valid: boolean;
  expected: string;
  actual: string;
}

/**
 * Verify data integrity with detailed result.
 *
 * @param data - Binary data to verify
 * @param expected - Expected SHA256 hash (hex-encoded)
 * @returns Detailed verification result
 */
export async function verifyChecksumDetailed(
  data: Uint8Array,
  expected: string
): Promise<ChecksumResult> {
  const actual = await computeChecksum(data);
  return {
    valid: actual === expected,
    expected,
    actual,
  };
}
